import { useMemo } from "react";
import { Trans, useLingui } from "@lingui/react/macro";
import {
  ALL_FEATURE_IDS,
  EXTRA_FEATURES,
  MODULE_GROUPS,
  type ModuleFeatureGroup,
} from "../features-catalog.js";

// Reusable feature picker: the same UI that NewConfig uses, so remote configs and
// licences agree on the exact catalogue of feature ids.

interface FeatureSelectorProps {
  features: Set<string>;
  onChange: (next: Set<string>) => void;
  legend?: string;
}

export function FeatureSelector({ features, onChange, legend }: FeatureSelectorProps) {
  const { t, i18n } = useLingui();
  const toggleFeature = (id: string) => {
    const next = new Set(features);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onChange(next);
  };
  const setAll = (ids: string[], on: boolean) => {
    const next = new Set(features);
    for (const id of ids) {
      if (on) next.add(id);
      else next.delete(id);
    }
    onChange(next);
  };

  const allSqlIds = useMemo(
    () => [
      EXTRA_FEATURES.dbCross.id,
      ...MODULE_GROUPS.map((g) => g.sqlRead).filter((v): v is string => !!v),
    ],
    [],
  );
  const allWebApiReadIds = useMemo(
    () => MODULE_GROUPS.map((g) => g.webApiRead).filter((v): v is string => !!v),
    [],
  );
  const allWebApiWriteIds = useMemo(
    () =>
      MODULE_GROUPS.flatMap((g) => [g.webApiWrite, g.webApiPost]).filter(
        (v): v is string => !!v,
      ),
    [],
  );

  return (
    <fieldset>
      <legend>
        {legend ?? t`Features`} <Trans>({features.size} of {ALL_FEATURE_IDS.length} active)</Trans>
      </legend>
      <p className="muted small">
        <Trans>
          Pick what the LLM is allowed to touch. <code>.write</code> and{" "}
          <code>.read</code> per module control the Sage Web API; <code>db.*.read</code>{" "}
          control the read-only SQL surface. See <code>docs/permissions.md</code> for the
          full reference.
        </Trans>
      </p>

      <div className="features-quick">
        <button type="button" className="ghost" onClick={() => setAll(allSqlIds, true)}>
          <Trans>Select all SQL read</Trans>
        </button>
        <button
          type="button"
          className="ghost"
          onClick={() => setAll(allWebApiReadIds, true)}
        >
          <Trans>Select all Web API read</Trans>
        </button>
        <button
          type="button"
          className="ghost"
          onClick={() => setAll(allWebApiWriteIds, true)}
          title={t`.write and .post still require the write-mode checkbox on the remote config`}
        >
          <Trans>Select all Web API write + post</Trans>
        </button>
        <button
          type="button"
          className="ghost"
          onClick={() => onChange(new Set(ALL_FEATURE_IDS))}
        >
          <Trans>Everything</Trans>
        </button>
        <button
          type="button"
          className="ghost danger"
          onClick={() => onChange(new Set())}
        >
          <Trans>Clear</Trans>
        </button>
      </div>

      <div className="features-cross">
        <label className="inline">
          <input
            type="checkbox"
            checked={features.has(EXTRA_FEATURES.discovery.id)}
            onChange={() => toggleFeature(EXTRA_FEATURES.discovery.id)}
          />{" "}
          <strong>{EXTRA_FEATURES.discovery.id}</strong> —{" "}
          {i18n._(EXTRA_FEATURES.discovery.label)}
        </label>
        <label className="inline">
          <input
            type="checkbox"
            checked={features.has(EXTRA_FEATURES.dbCross.id)}
            onChange={() => toggleFeature(EXTRA_FEATURES.dbCross.id)}
          />{" "}
          <strong>{EXTRA_FEATURES.dbCross.id}</strong> —{" "}
          {i18n._(EXTRA_FEATURES.dbCross.label)}
        </label>
        <label className="inline">
          <input
            type="checkbox"
            checked={features.has(EXTRA_FEATURES.advancedDocumentScripts.id)}
            onChange={() => toggleFeature(EXTRA_FEATURES.advancedDocumentScripts.id)}
          />{" "}
          <strong>{EXTRA_FEATURES.advancedDocumentScripts.id}</strong> —{" "}
          {i18n._(EXTRA_FEATURES.advancedDocumentScripts.label)}
        </label>
      </div>

      <table className="features-table">
        <thead>
          <tr>
            <th><Trans>Module</Trans></th>
            <th title={t`Read-only SQL query tool (sage300_db_*_query)`}><Trans>SQL read</Trans></th>
            <th title={t`OData list / get / search`}><Trans>Web API read</Trans></th>
            <th title={t`OData create / update / delete`}><Trans>Web API write</Trans></th>
            <th title={t`Irreversible postings (batches, adjustments)`}><Trans>Web API post</Trans></th>
          </tr>
        </thead>
        <tbody>
          {MODULE_GROUPS.map((g) => (
            <ModuleRow
              key={g.code}
              group={g}
              features={features}
              onToggle={toggleFeature}
            />
          ))}
        </tbody>
      </table>
    </fieldset>
  );
}

interface ModuleRowProps {
  group: ModuleFeatureGroup;
  features: Set<string>;
  onToggle: (id: string) => void;
}

function ModuleRow({ group, features, onToggle }: ModuleRowProps) {
  const { i18n } = useLingui();
  return (
    <tr>
      <td>
        <strong>{i18n._(group.label)}</strong>
        <span className="muted small"> ({group.code})</span>
      </td>
      <FeatureCell id={group.sqlRead} features={features} onToggle={onToggle} />
      <FeatureCell id={group.webApiRead} features={features} onToggle={onToggle} />
      <FeatureCell id={group.webApiWrite} features={features} onToggle={onToggle} />
      <FeatureCell id={group.webApiPost} features={features} onToggle={onToggle} />
    </tr>
  );
}

function FeatureCell({
  id,
  features,
  onToggle,
}: {
  id: string | undefined;
  features: Set<string>;
  onToggle: (id: string) => void;
}) {
  const { t } = useLingui();
  if (!id) {
    return <td className="feature-cell empty" aria-label={t`not applicable`}>—</td>;
  }
  return (
    <td className="feature-cell">
      <label title={id}>
        <input
          type="checkbox"
          checked={features.has(id)}
          onChange={() => onToggle(id)}
        />
        <span className="feature-id">{id}</span>
      </label>
    </td>
  );
}
